import { api } from './api';
import type {
  ScorecardBoard, ScorecardCadence, ScorecardDetail, ScorecardMetric, ScorecardObservation,
  ScorecardReadingInput, ScorecardIssue, ScorecardIssueInput, ScorecardView,
  QualifiedOnboardingCall, QualifiedOnboardingCallInput,
} from '../types/admin';
import type {
  AdminOrganizationDetail,
  AdminOrganizationListItem,
  AdminOrganizationListParams,
  AdminPlatformSummary,
  BoardStatusOrder,
  PaginatedResult,
  PlatformBoard,
  PlatformBoardKind,
  PlatformBoardStatus,
  StaffProfile,
} from '../types/admin';
import type { ApexCrmImportReport } from '../types/admin';
import type { Lead, LeadListParams, LeadStageChange, LifecycleStage } from '../types/admin';
import type { DevTicket, DevTicketListParams, DevTicketSupportLink, DevTicketAttachment, DevTicketChecklistItem } from '../types/admin';
import type { CustomerRecord, OrgHealthSnapshot, PlatformActivity, StaffTask } from '../types/admin';
import type { AssignableStaff, DevTicketComment, StaffEmailAccount } from '../types/admin';
import type {
  NpsCampaign,
  NpsResponse,
  NpsSummary,
  OnboardingEnrollment,
  OnboardingSequence,
  OnboardingStep,
  PlatformEmailTemplate,
  PlatformEmailTemplateFolder,
} from '../types/admin';

/** Drops empty values so callers can pass partial param objects freely. */
const buildQuery = (params: Record<string, string | number | boolean | undefined>): string => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== false) qs.append(key, String(value));
  });
  const query = qs.toString();
  return query ? `?${query}` : '';
};

export const AdminScorecardAPI = {
  async board(cadence: ScorecardCadence, period_start?: string): Promise<ScorecardBoard> {
    return (await api.get<ScorecardBoard>(`/api/admin/scorecard/${buildQuery({ cadence, period_start })}`)).data;
  },
  async detail(key: string, period_start: string): Promise<ScorecardDetail> {
    return (await api.get<ScorecardDetail>(`/api/admin/scorecard/metrics/${key}/${buildQuery({ period_start })}`)).data;
  },
  async settings(key: string, data: Pick<ScorecardMetric, 'owner' | 'target' | 'direction' | 'revision'>): Promise<ScorecardMetric> {
    return (await api.patch<ScorecardMetric>(`/api/admin/scorecard/metrics/${key}/`, data)).data;
  },
  async reading(key: string, data: ScorecardReadingInput): Promise<ScorecardObservation> {
    return (await api.post<ScorecardObservation>(`/api/admin/scorecard/metrics/${key}/reading/`, data)).data;
  },
  async refresh(): Promise<{ updated: string[]; skipped: string[] }> {
    return (await api.post<{ updated: string[]; skipped: string[] }>('/api/admin/scorecard/refresh/', {})).data;
  },
  async export(cadence: ScorecardCadence, period_start: string, division: ScorecardView): Promise<string> {
    return (await api.get<string>(`/api/admin/scorecard/export/${buildQuery({ cadence, period_start, division })}`)).data;
  },
  async issues(page = 1): Promise<PaginatedResult<ScorecardIssue>> {
    return (await api.get<PaginatedResult<ScorecardIssue>>(`/api/admin/scorecard/issues/${buildQuery({ page, page_size: 25 })}`)).data;
  },
  async createIssue(key: string, data: ScorecardIssueInput): Promise<ScorecardIssue> {
    return (await api.post<ScorecardIssue>(`/api/admin/scorecard/metrics/${key}/issues/`, data)).data;
  },
  async updateIssue(id: number, data: Omit<ScorecardIssueInput, 'period_start'> & { revision: number }): Promise<ScorecardIssue> {
    return (await api.patch<ScorecardIssue>(`/api/admin/scorecard/issues/${id}/`, data)).data;
  },
  async resolveIssue(id: number, revision: number, resolution: string): Promise<ScorecardIssue> {
    return (await api.post<ScorecardIssue>(`/api/admin/scorecard/issues/${id}/resolve/`, { revision, resolution })).data;
  },
  async calls(page = 1): Promise<PaginatedResult<QualifiedOnboardingCall>> {
    return (await api.get<PaginatedResult<QualifiedOnboardingCall>>(`/api/admin/scorecard/onboarding-calls/${buildQuery({ page, page_size: 15, include_voided: true })}`)).data;
  },
  async createCall(data: QualifiedOnboardingCallInput): Promise<QualifiedOnboardingCall> {
    return (await api.post<QualifiedOnboardingCall>('/api/admin/scorecard/onboarding-calls/', data)).data;
  },
  async voidCall(id: string, reason: string): Promise<QualifiedOnboardingCall> {
    return (await api.post<QualifiedOnboardingCall>(`/api/admin/scorecard/onboarding-calls/${id}/void/`, { reason })).data;
  },
};

/**
 * Client for the platform SuperAdmin dashboard API.
 * All endpoints are gated server-side by IsSuperuser (Django superusers only).
 */
export const AdminAPI = {
  async getPlatformSummary(): Promise<AdminPlatformSummary> {
    const res = await api.get<AdminPlatformSummary>('/api/admin/summary/');
    return res.data;
  },

  async getOrganizations(
    params: AdminOrganizationListParams = {}
  ): Promise<PaginatedResult<AdminOrganizationListItem>> {
    const qs = new URLSearchParams();
    if (params.search) qs.append('search', params.search);
    if (params.status) qs.append('status', params.status);
    if (params.staleness) qs.append('staleness', params.staleness);
    if (params.account_view) qs.append('account_view', params.account_view);
    if (params.has_open_tickets) qs.append('has_open_tickets', 'true');
    if (params.ordering) qs.append('ordering', params.ordering);
    if (params.page) qs.append('page', String(params.page));
    if (params.page_size) qs.append('page_size', String(params.page_size));
    const query = qs.toString();
    const endpoint = `/api/admin/organizations/${query ? `?${query}` : ''}`;
    const res = await api.get<PaginatedResult<AdminOrganizationListItem>>(endpoint);
    return res.data;
  },

  async getOrganization(id: string): Promise<AdminOrganizationDetail> {
    const res = await api.get<AdminOrganizationDetail>(`/api/admin/organizations/${id}/`);
    return res.data;
  },
};

/**
 * Client for the Admin hub (Apex-internal CRM, development board, onboarding).
 *
 * Separate namespace from AdminAPI above because these endpoints serve Apex's
 * own data rather than cross-organization views of customers — but the same
 * shared `api` client and the same server-side IsSuperuser boundary.
 */
export const AdminStaffAPI = {
  /** Everybody who can be assigned work — one list for every picker. */
  async assignable(): Promise<AssignableStaff[]> {
    const res = await api.get<AssignableStaff[]>('/api/admin/staff/assignable/');
    return res.data;
  },

  async list(): Promise<PaginatedResult<StaffProfile>> {
    const res = await api.get<PaginatedResult<StaffProfile>>('/api/admin/staff/');
    return res.data;
  },

  async update(id: number, changes: Partial<StaffProfile>): Promise<StaffProfile> {
    const res = await api.patch<StaffProfile>(`/api/admin/staff/${id}/`, changes);
    return res.data;
  },
};

export const AdminBoardsAPI = {
  async list(kind?: PlatformBoardKind): Promise<PaginatedResult<PlatformBoard>> {
    const res = await api.get<PaginatedResult<PlatformBoard>>(
      `/api/admin/boards/${buildQuery({ kind })}`
    );
    return res.data;
  },

  async get(id: number): Promise<PlatformBoard> {
    const res = await api.get<PlatformBoard>(`/api/admin/boards/${id}/`);
    return res.data;
  },

  async listStatuses(boardId: number): Promise<PaginatedResult<PlatformBoardStatus>> {
    const res = await api.get<PaginatedResult<PlatformBoardStatus>>(
      `/api/admin/board-statuses/${buildQuery({ board: boardId })}`
    );
    return res.data;
  },

  async createStatus(status: Partial<PlatformBoardStatus>): Promise<PlatformBoardStatus> {
    const res = await api.post<PlatformBoardStatus>('/api/admin/board-statuses/', status);
    return res.data;
  },

  async updateStatus(id: number, changes: Partial<PlatformBoardStatus>): Promise<PlatformBoardStatus> {
    const res = await api.patch<PlatformBoardStatus>(`/api/admin/board-statuses/${id}/`, changes);
    return res.data;
  },

  async deleteStatus(id: number): Promise<void> {
    await api.delete(`/api/admin/board-statuses/${id}/`);
  },

  /** Persist a whole column order in one request. */
  async reorderStatuses(order: BoardStatusOrder[]): Promise<void> {
    await api.post('/api/admin/board-statuses/reorder/', order);
  },
};

export const AdminCrmAPI = {
  async listLeads(params: LeadListParams = {}): Promise<PaginatedResult<Lead>> {
    const res = await api.get<PaginatedResult<Lead>>(
      `/api/admin/crm/leads/${buildQuery({ ...params })}`
    );
    return res.data;
  },

  async getLead(id: string): Promise<Lead> {
    const res = await api.get<Lead>(`/api/admin/crm/leads/${id}/`);
    return res.data;
  },

  async createLead(lead: Partial<Lead>): Promise<Lead> {
    const res = await api.post<Lead>('/api/admin/crm/leads/', lead);
    return res.data;
  },

  async updateLead(id: string, changes: Partial<Lead>): Promise<Lead> {
    const res = await api.patch<Lead>(`/api/admin/crm/leads/${id}/`, changes);
    return res.data;
  },

  async deleteLead(id: string): Promise<void> {
    await api.delete(`/api/admin/crm/leads/${id}/`);
  },

  /**
   * Move a lead's lifecycle stage by hand. Separate from `updateLead` because
   * the stage is read-only over PATCH — it goes through the server's stage
   * writer so the history row is always recorded.
   */
  async setStage(id: string, stage: LifecycleStage, reason?: string): Promise<Lead> {
    const res = await api.post<Lead>(`/api/admin/crm/leads/${id}/set-stage/`, { stage, reason });
    return res.data;
  },

  /** Drop a lead into a board column. The server reindexes the column densely. */
  async moveLead(id: string, boardStatus: number, position: number, reason?: string): Promise<Lead> {
    const res = await api.post<Lead>(`/api/admin/crm/leads/${id}/move/`, {
      board_status: boardStatus,
      position,
      ...(reason ? { reason } : {}),
    });
    return res.data;
  },

  async getTimeline(id: string, kind?: string, page = 1): Promise<PaginatedResult<PlatformActivity>> {
    const res = await api.get<PaginatedResult<PlatformActivity>>(
      `/api/admin/crm/leads/${id}/timeline/${buildQuery({ kind, page, page_size: 50 })}`
    );
    return res.data;
  },

  /** Log an activity by hand. */
  async logActivity(
    id: string,
    entry: { kind: string; summary: string; body?: string; direction?: 'outbound' | 'inbound' }
  ): Promise<PlatformActivity> {
    const res = await api.post<PlatformActivity>(`/api/admin/crm/leads/${id}/timeline/`, entry);
    return res.data;
  },

  /**
   * Migrate an ApexCRM export into the pipeline.
   *
   * `dryRun` is the modal's preview step. The server runs the whole import and
   * rolls it back, so the report it returns is what a commit produces — there
   * is no second estimating code path that could drift from the real one.
   */
  async importApexCrm(file: File, dryRun: boolean): Promise<ApexCrmImportReport> {
    const form = new FormData();
    form.append('file', file);
    form.append('dry_run', String(dryRun));
    const res = await api.post<ApexCrmImportReport>('/api/admin/crm/import/', form);
    return res.data;
  },

  /** Send from the signed-in staff member's own connected mailbox. */
  async sendEmail(id: string, subject: string, body: string, to?: string, body_html?: string) {
    const res = await api.post(`/api/admin/crm/leads/${id}/email/`, {
      subject,
      body,
      ...(to !== undefined ? { to } : {}),
      ...(body_html ? { body_html } : {}),
    });
    return res.data;
  },

  /** Only notes, calls and meetings can be removed; the server refuses the rest. */
  async deleteActivity(activityId: string): Promise<void> {
    await api.delete(`/api/admin/activities/${activityId}/`);
  },

  async getStageHistory(id: string): Promise<LeadStageChange[]> {
    const res = await api.get<LeadStageChange[]>(`/api/admin/crm/leads/${id}/stage-history/`);
    return res.data;
  },
};

export const AdminDevAPI = {
  async listTickets(params: DevTicketListParams = {}): Promise<PaginatedResult<DevTicket>> {
    const res = await api.get<PaginatedResult<DevTicket>>(
      `/api/admin/dev/tickets/${buildQuery({ ...params })}`
    );
    return res.data;
  },

  async ticketActivity(id: number, page = 1) {
    return (
      await api.get<{
        count: number;
        next: string | null;
        results: Array<{
          id: number;
          actor_name: string;
          summary: string;
          created_at: string;
          changes: Record<string, { before: string; after: string }>;
        }>;
      }>(`/api/admin/dev/tickets/${id}/activity/?page=${page}`)
    ).data;
  },

  async getTicket(id: number): Promise<DevTicket> {
    const res = await api.get<DevTicket>(`/api/admin/dev/tickets/${id}/`);
    return res.data;
  },

  async createTicket(ticket: Partial<DevTicket> | FormData): Promise<DevTicket> {
    const res = await api.post<DevTicket>('/api/admin/dev/tickets/', ticket);
    return res.data;
  },

  async updateTicket(id: number, changes: Partial<DevTicket> | FormData): Promise<DevTicket> {
    const res = await api.patch<DevTicket>(`/api/admin/dev/tickets/${id}/`, changes);
    return res.data;
  },

  async deleteTicket(id: number): Promise<void> {
    await api.delete(`/api/admin/dev/tickets/${id}/`);
  },

  async archiveTicket(id: number): Promise<DevTicket> {
    const res = await api.post<DevTicket>(`/api/admin/dev/tickets/${id}/archive/`, {});
    return res.data;
  },

  async restoreTicket(id: number): Promise<DevTicket> {
    const res = await api.post<DevTicket>(`/api/admin/dev/tickets/${id}/restore/`, {});
    return res.data;
  },

  async moveTicket(id: number, boardStatus: number, position: number): Promise<DevTicket> {
    const res = await api.post<DevTicket>(`/api/admin/dev/tickets/${id}/move/`, {
      board_status: boardStatus,
      position,
    });
    return res.data;
  },

  /** Mark shipped and tell every linked support ticket. */
  async shipTicket(id: number, releaseVersion: string): Promise<DevTicket & { tickets_notified: number }> {
    const res = await api.post<DevTicket & { tickets_notified: number }>(
      `/api/admin/dev/tickets/${id}/ship/`,
      { release_version: releaseVersion }
    );
    return res.data;
  },

  async linkSupportTicket(
    id: number,
    supportTicketId: string,
    notifyCustomer = false
  ): Promise<DevTicketSupportLink> {
    const res = await api.post<DevTicketSupportLink>(
      `/api/admin/dev/tickets/${id}/link-support-ticket/`,
      { support_ticket: supportTicketId, notify_customer: notifyCustomer }
    );
    return res.data;
  },

  async unlinkSupportTicket(id: number, linkId: number): Promise<void> {
    await api.delete(`/api/admin/dev/tickets/${id}/unlink-support-ticket/${linkId}/`);
  },

  async listComments(ticketId: number, page = 1): Promise<PaginatedResult<DevTicketComment>> {
    const res = await api.get<PaginatedResult<DevTicketComment>>(
      `/api/admin/dev/comments/${buildQuery({ dev_ticket: ticketId, page, page_size: 30, ordering: '-created_at' })}`
    );
    return res.data;
  },

  async addComment(ticketId: number, body: string, files: File[] = []): Promise<DevTicketComment> {
    const form = new FormData();
    form.append('dev_ticket', String(ticketId));
    form.append('body', body);
    files.forEach(file => form.append('files', file));
    const res = await api.post<DevTicketComment>('/api/admin/dev/comments/', files.length ? form : { dev_ticket: ticketId, body });
    return res.data;
  },

  async updateComment(id: number, body: string): Promise<DevTicketComment> {
    return (await api.patch<DevTicketComment>(`/api/admin/dev/comments/${id}/`, { body })).data;
  },

  async listTags(): Promise<string[]> {
    return (await api.get<string[]>('/api/admin/dev/tickets/tags/')).data;
  },

  async uploadAttachments(id: number, files: File[]): Promise<DevTicketAttachment[]> {
    const form = new FormData();
    files.forEach(file => form.append('files', file));
    return (await api.post<DevTicketAttachment[]>(`/api/admin/dev/tickets/${id}/attachments/`, form)).data;
  },

  async removeAttachment(id: number, fileId: number): Promise<void> {
    await api.delete(`/api/admin/dev/tickets/${id}/attachments/${fileId}/`);
  },

  async addChecklistItem(id: number, text: string): Promise<DevTicketChecklistItem> {
    return (await api.post<DevTicketChecklistItem>(`/api/admin/dev/tickets/${id}/checklist/`, { text })).data;
  },

  async updateChecklistItem(id: number, itemId: number, changes: Partial<DevTicketChecklistItem>): Promise<DevTicketChecklistItem> {
    return (await api.patch<DevTicketChecklistItem>(`/api/admin/dev/tickets/${id}/checklist/${itemId}/`, changes)).data;
  },

  async deleteChecklistItem(id: number, itemId: number): Promise<void> {
    await api.delete(`/api/admin/dev/tickets/${id}/checklist/${itemId}/`);
  },

  async deleteComment(id: number): Promise<void> {
    await api.delete(`/api/admin/dev/comments/${id}/`);
  },
};

export const AdminOnboardingAPI = {
  async listCustomers(params: {
    search?: string;
    band?: string;
    cs_owner?: string;
    include_internal?: boolean;
    page?: number;
    page_size?: number;
  } = {}): Promise<PaginatedResult<CustomerRecord>> {
    const res = await api.get<PaginatedResult<CustomerRecord>>(
      `/api/admin/onboarding/customers/${buildQuery({ ...params })}`
    );
    return res.data;
  },

  async getCustomer(id: string): Promise<CustomerRecord> {
    const res = await api.get<CustomerRecord>(`/api/admin/onboarding/customers/${id}/`);
    return res.data;
  },

  async updateCustomer(id: string, changes: Partial<CustomerRecord>): Promise<CustomerRecord> {
    const res = await api.patch<CustomerRecord>(`/api/admin/onboarding/customers/${id}/`, changes);
    return res.data;
  },

  /** Take a health reading now rather than waiting for tonight. */
  async snapshotCustomer(id: string): Promise<OrgHealthSnapshot> {
    const res = await api.post<OrgHealthSnapshot>(`/api/admin/onboarding/customers/${id}/snapshot/`, {});
    return res.data;
  },

  async snoozeCustomer(id: string, days = 14): Promise<CustomerRecord> {
    const res = await api.post<CustomerRecord>(`/api/admin/onboarding/customers/${id}/snooze/`, { days });
    return res.data;
  },

  async listTasks(
    params: {
      state?: 'open' | 'done' | 'all';
      assignee?: string;
      organization?: string;
      lead?: string;
      dev_ticket?: number;
      page?: number;
      page_size?: number;
    } = {}
  ) {
    const res = await api.get<PaginatedResult<StaffTask>>(
      `/api/admin/onboarding/tasks/${buildQuery({ ...params })}`
    );
    return res.data;
  },

  async createTask(task: Partial<StaffTask>): Promise<StaffTask> {
    const res = await api.post<StaffTask>('/api/admin/onboarding/tasks/', task);
    return res.data;
  },

  async updateTask(id: string, changes: Partial<StaffTask>): Promise<StaffTask> {
    const res = await api.patch<StaffTask>(`/api/admin/onboarding/tasks/${id}/`, changes);
    return res.data;
  },

  async completeTask(id: string): Promise<StaffTask> {
    const res = await api.post<StaffTask>(`/api/admin/onboarding/tasks/${id}/complete/`, {});
    return res.data;
  },

  async deleteTask(id: string): Promise<void> {
    await api.delete(`/api/admin/onboarding/tasks/${id}/`);
  },

  async reopenTask(id: string): Promise<StaffTask> {
    const res = await api.post<StaffTask>(`/api/admin/onboarding/tasks/${id}/reopen/`, {});
    return res.data;
  },
};

/** The signed-in staff member's own connected mailboxes. */
export const AdminEmailAPI = {
  async listAccounts(): Promise<StaffEmailAccount[]> {
    const res = await api.get<StaffEmailAccount[]>('/api/admin/email/accounts/');
    return res.data;
  },

  /** Returns the provider URL to send the browser to. */
  async connectUrl(provider: 'gmail' | 'outlook', returnTo?: string): Promise<string> {
    const res = await api.get<{ auth_url: string }>(
      `/api/admin/email/connect/${provider}/${buildQuery({ return_to: returnTo })}`
    );
    return res.data.auth_url;
  },

  async disconnect(id: string): Promise<void> {
    await api.delete(`/api/admin/email/accounts/${id}/`);
  },
};

/**
 * Onboarding drip sequences: the templates, the sequences built from them, and
 * who is currently receiving one.
 */
export const AdminSequencesAPI = {
  async listTemplates(page?: number): Promise<PaginatedResult<PlatformEmailTemplate>> {
    const loadPage = async (number: number) => (await api.get<PaginatedResult<PlatformEmailTemplate>>(
      `/api/admin/onboarding/templates/?page_size=200&page=${number}`
    )).data;
    const result = await loadPage(page ?? 1);
    if (page !== undefined) return result;
    let next = result.next;
    let number = 2;
    while (next) {
      const batch = await loadPage(number++);
      result.results.push(...batch.results);
      next = batch.next;
    }
    return { ...result, next: null };
  },

  async listTemplateFolders(): Promise<PlatformEmailTemplateFolder[]> {
    const folders: PlatformEmailTemplateFolder[] = [];
    let page = 1;
    let next: string | null;
    do {
      const { data } = await api.get<PaginatedResult<PlatformEmailTemplateFolder>>(
        `/api/admin/onboarding/template-folders/?page_size=200&page=${page++}`
      );
      folders.push(...data.results);
      next = data.next;
    } while (next);
    return folders;
  },

  async createTemplateFolder(name: string): Promise<PlatformEmailTemplateFolder> {
    return (await api.post<PlatformEmailTemplateFolder>('/api/admin/onboarding/template-folders/', { name })).data;
  },

  async renameTemplateFolder(id: string, name: string): Promise<PlatformEmailTemplateFolder> {
    return (await api.patch<PlatformEmailTemplateFolder>(`/api/admin/onboarding/template-folders/${id}/`, { name })).data;
  },

  async deleteTemplateFolder(id: string): Promise<void> {
    await api.delete(`/api/admin/onboarding/template-folders/${id}/`);
  },

  async createTemplate(template: Partial<PlatformEmailTemplate>): Promise<PlatformEmailTemplate> {
    const res = await api.post<PlatformEmailTemplate>('/api/admin/onboarding/templates/', template);
    return res.data;
  },

  async updateTemplate(
    id: string,
    changes: Partial<PlatformEmailTemplate>
  ): Promise<PlatformEmailTemplate> {
    const res = await api.patch<PlatformEmailTemplate>(
      `/api/admin/onboarding/templates/${id}/`,
      changes
    );
    return res.data;
  },

  async deleteTemplate(id: string): Promise<void> {
    await api.delete(`/api/admin/onboarding/templates/${id}/`);
  },

  async listSequences(): Promise<PaginatedResult<OnboardingSequence>> {
    const res = await api.get<PaginatedResult<OnboardingSequence>>(
      '/api/admin/onboarding/sequences/?page_size=100'
    );
    return res.data;
  },

  async getSequence(id: string): Promise<OnboardingSequence> {
    const res = await api.get<OnboardingSequence>(`/api/admin/onboarding/sequences/${id}/`);
    return res.data;
  },

  async createSequence(sequence: Partial<OnboardingSequence>): Promise<OnboardingSequence> {
    const res = await api.post<OnboardingSequence>('/api/admin/onboarding/sequences/', sequence);
    return res.data;
  },

  async updateSequence(
    id: string,
    changes: Partial<OnboardingSequence>
  ): Promise<OnboardingSequence> {
    const res = await api.patch<OnboardingSequence>(
      `/api/admin/onboarding/sequences/${id}/`,
      changes
    );
    return res.data;
  },

  async deleteSequence(id: string): Promise<void> {
    await api.delete(`/api/admin/onboarding/sequences/${id}/`);
  },

  /** Put one customer on this sequence by hand; returns the enrollment. */
  async enrol(sequenceId: string, customerId: string): Promise<OnboardingEnrollment> {
    const res = await api.post<OnboardingEnrollment>(
      `/api/admin/onboarding/sequences/${sequenceId}/enrol/`,
      { customer: customerId }
    );
    return res.data;
  },

  async createStep(step: Partial<OnboardingStep>): Promise<OnboardingStep> {
    const res = await api.post<OnboardingStep>('/api/admin/onboarding/steps/', step);
    return res.data;
  },

  async updateStep(id: string, changes: Partial<OnboardingStep>): Promise<OnboardingStep> {
    const res = await api.patch<OnboardingStep>(`/api/admin/onboarding/steps/${id}/`, changes);
    return res.data;
  },

  async deleteStep(id: string): Promise<void> {
    await api.delete(`/api/admin/onboarding/steps/${id}/`);
  },

  async listEnrollments(
    params: { customer?: string; sequence?: string; status?: string; page_size?: number } = {}
  ): Promise<PaginatedResult<OnboardingEnrollment>> {
    const res = await api.get<PaginatedResult<OnboardingEnrollment>>(
      `/api/admin/onboarding/enrollments/${buildQuery({ ...params })}`
    );
    return res.data;
  },

  /** Stops future sends without erasing what already went out. */
  async cancelEnrollment(id: string): Promise<OnboardingEnrollment> {
    const res = await api.post<OnboardingEnrollment>(
      `/api/admin/onboarding/enrollments/${id}/cancel/`,
      {}
    );
    return res.data;
  },
};

/** NPS: the campaign that decides who gets asked, and what came back. */
export const AdminNpsAPI = {
  async listCampaigns(): Promise<PaginatedResult<NpsCampaign>> {
    const res = await api.get<PaginatedResult<NpsCampaign>>('/api/admin/nps/campaigns/?page_size=50');
    return res.data;
  },

  async createCampaign(campaign: Partial<NpsCampaign>): Promise<NpsCampaign> {
    const res = await api.post<NpsCampaign>('/api/admin/nps/campaigns/', campaign);
    return res.data;
  },

  async updateCampaign(id: string, changes: Partial<NpsCampaign>): Promise<NpsCampaign> {
    const res = await api.patch<NpsCampaign>(`/api/admin/nps/campaigns/${id}/`, changes);
    return res.data;
  },

  /** Activating one campaign deactivates the rest, server-side. */
  async activateCampaign(id: string): Promise<NpsCampaign> {
    const res = await api.post<NpsCampaign>(`/api/admin/nps/campaigns/${id}/activate/`, {});
    return res.data;
  },

  async deactivateCampaign(id: string): Promise<NpsCampaign> {
    const res = await api.post<NpsCampaign>(`/api/admin/nps/campaigns/${id}/deactivate/`, {});
    return res.data;
  },

  async listResponses(
    params: { category?: string; resolved?: string; page_size?: number } = {}
  ): Promise<PaginatedResult<NpsResponse>> {
    const res = await api.get<PaginatedResult<NpsResponse>>(
      `/api/admin/nps/responses/${buildQuery({ ...params })}`
    );
    return res.data;
  },

  async updateResponse(id: string, changes: Partial<NpsResponse>): Promise<NpsResponse> {
    const res = await api.patch<NpsResponse>(`/api/admin/nps/responses/${id}/`, changes);
    return res.data;
  },

  async summary(): Promise<NpsSummary> {
    const res = await api.get<NpsSummary>('/api/admin/nps/responses/summary/');
    return res.data;
  },
};
